import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { collection, query, where, orderBy, onSnapshot, type Timestamp } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/lib/firebase'
import { toast } from 'sonner'

export type ImprovementStatus = 'suggested' | 'in-progress' | 'done' | 'dismissed'

export type ImprovementCategory =
  | 'security' | 'docs' | 'tests' | 'branding' | 'monetization'
  | 'seo' | 'compliance' | 'performance' | 'product' | 'legal'
  | 'distribution' | 'data'

export type ImprovementImpact = 'low' | 'medium' | 'high'
export type ImprovementEffort = 'quick' | 'medium' | 'deep'

export interface CrossAppOpportunity {
  other_app_slug: string
  other_app_name: string
  type: 'reuse' | 'merge' | 'bundle' | 'cross_sell'
  rationale: string
}

export interface Improvement {
  id: string
  app_slug: string
  app_id: string
  app_name: string
  app_entity: string
  title: string
  category: ImprovementCategory
  impact: ImprovementImpact
  effort: ImprovementEffort
  rationale: string
  expected_uplift: string
  prompt: string
  cross_app_opportunities?: CrossAppOpportunity[]
  status: ImprovementStatus
  generated_at: string
  generated_iteration: number
  notes?: string
  dismissed_reason?: string
  started_at?: string
  completed_at?: string
  created_by?: string
  last_updated_by?: string
  updated_at?: string | Timestamp
}

/** Live snapshot of all improvements (across all apps), newest iteration first. */
export function useAllImprovements() {
  const [items, setItems] = useState<Improvement[]>([])
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    const q = query(collection(db, 'improvements'), orderBy('generated_at', 'desc'))
    const unsub = onSnapshot(q, (snap) => {
      setItems(snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<Improvement, 'id'>) })))
      setLoading(false)
    }, () => setLoading(false))
    return () => unsub()
  }, [])
  return { items, loading }
}

/** Live snapshot for a single app. */
export function useAppImprovements(appSlug: string | undefined) {
  const [items, setItems] = useState<Improvement[]>([])
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    // Reset on every appSlug change so the previous app's rows don't linger
    // while the next app's data loads.
    setItems([]); setLoading(true)
    if (!appSlug) { setLoading(false); return }
    const q = query(collection(db, 'improvements'), where('app_slug', '==', appSlug), orderBy('generated_at', 'desc'))
    const unsub = onSnapshot(q, (snap) => {
      setItems(snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<Improvement, 'id'>) })))
      setLoading(false)
    }, () => setLoading(false))
    return () => unsub()
  }, [appSlug])
  return { items, loading }
}

/* Mutations */

export function useGenerateImprovementAudit() {
  const fn = httpsCallable<{ slug: string }, { ok: boolean; items_added?: number; iteration?: number }>(
    functions, 'generateImprovementAudit'
  )
  return useMutation({
    mutationFn: async (slug: string) => (await fn({ slug })).data,
    onSuccess: (r) => {
      toast.success(`Audit complete · ${r.items_added ?? 0} new items · iteration ${r.iteration ?? 1}`)
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useUpdateImprovement() {
  const fn = httpsCallable<{ itemId: string; status?: ImprovementStatus; notes?: string; dismissed_reason?: string }, { ok: boolean }>(
    functions, 'updateImprovement'
  )
  return useMutation({
    mutationFn: async (input: { itemId: string; status?: ImprovementStatus; notes?: string; dismissed_reason?: string }) => (await fn(input)).data,
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useBulkUpdateImprovements() {
  const fn = httpsCallable<{ itemIds: string[]; status: ImprovementStatus }, { ok: boolean; updated: number }>(
    functions, 'bulkUpdateImprovements'
  )
  return useMutation({
    mutationFn: async (input: { itemIds: string[]; status: ImprovementStatus }) => (await fn(input)).data,
    onSuccess: (r) => toast.success(`Updated ${r.updated} items`),
    onError: (e: Error) => toast.error(e.message),
  })
}
